import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import { FontWeight } from '@freelancer/ui/text';
import { required } from '@freelancer/ui/validators';
import * as Rx from 'rxjs';

enum ReportStep {
  CATEGORY = 1,
  DESCRIPTION = 2,
}

@Component({
  template: `
    <fl-bit flTrackingSection="ContestViewPage.UserReportModal">
      <fl-heading
        class="UserReport-header"
        i18n="User report header"
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H1"
        [flMarginBottom]="Margin.SMALL"
      >
        Help us understand what's happening
      </fl-heading>

      <ng-container *ngIf="currentReportStep$ | async as currentReportStep">
        <ng-container [ngSwitch]="currentReportStep">
          <ng-container *ngSwitchCase="ReportStep.CATEGORY">
            <fl-heading
              i18n="User report category header"
              [size]="HeadingSize.XXSMALL"
              [headingType]="HeadingType.H2"
              [flMarginBottom]="Margin.XXSMALL"
            >
              What's going on?
            </fl-heading>
            <fl-radio
              flTrackingLabel="SelectReportCategory"
              [control]="formGroup.get(ReportStep[currentReportStep])"
              [options]="categoryOptions"
            ></fl-radio>
          </ng-container>

          <ng-container *ngSwitchCase="ReportStep.DESCRIPTION">
            <fl-heading
              i18n="User report description header"
              [size]="HeadingSize.XXSMALL"
              [headingType]="HeadingType.H2"
              [flMarginBottom]="Margin.XXSMALL"
            >
              Please describe the issue
            </fl-heading>
            <fl-textarea
              placeholder="Enter issue description"
              i18n-placeholder="Enter description text"
              flTrackingLabel="EnterIssueDescription"
              [control]="formGroup.get(ReportStep[currentReportStep])"
              [rows]="6"
              [flMarginBottom]="Margin.XSMALL"
            >
            </fl-textarea>
          </ng-container>
        </ng-container>

        <ng-container
          *ngIf="
            formGroup.get(ReportStep[currentReportStep]) as currentFormControl
          "
        >
          <fl-bit class="UserReport-cta" [flHideMobile]="true">
            <fl-button
              i18n="Cancel button"
              flTrackingLabel="ClickCancelButton"
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              [flMarginRight]="Margin.SMALL"
              (click)="handleCancelClick()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="Continue button"
              flTrackingLabel="ClickContinueButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [disabled]="!currentFormControl.valid"
              (click)="handleContinueClick(currentReportStep)"
            >
              Continue
            </fl-button>
          </fl-bit>

          <fl-container
            class="UserReport-cta"
            [flSticky]="true"
            [flStickyBehaviour]="StickyBehaviour.ALWAYS"
            [flStickyPosition]="StickyPosition.BOTTOM"
            [flMarginBottom]="Margin.SMALL"
            [flHideTablet]="true"
            [flHideDesktop]="true"
          >
            <fl-button
              i18n="Cancel button"
              flTrackingLabel="ClickCancelButton"
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              [flMarginRight]="Margin.SMALL"
              (click)="handleCancelClick()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="Continue button"
              flTrackingLabel="ClickContinueButton"
              [color]="ButtonColor.SECONDARY"
              [size]="ButtonSize.SMALL"
              [disabled]="!currentFormControl.valid"
              (click)="handleContinueClick(currentReportStep)"
            >
              Continue
            </fl-button>
          </fl-container>
        </ng-container>
      </ng-container>
    </fl-bit>
  `,
  styleUrls: ['./user-report-modal.component.scss'],
})
export class UserReportModalComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  Margin = Margin;
  ReportStep = ReportStep;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  private currentReportStepSubject$ = new Rx.BehaviorSubject(
    ReportStep.CATEGORY,
  );
  currentReportStep$ = this.currentReportStepSubject$.asObservable();

  formGroup = new FormGroup({
    CATEGORY: new FormControl(null, required('You must choose a category')),
    DESCRIPTION: new FormControl(
      null,
      required('You must provide a description'),
    ),
  });

  categoryOptions: ReadonlyArray<string> = [
    'They are attempting to take payment or communication offsite',
    'They are posting misleading or fake profile information',
    'Their behavior is inappropriate or abusive',
    'There has been issues with their projects',
    'They submitted copied work',
    'Other',
  ];

  constructor(private modalRef: ModalRef<UserReportModalComponent>) {}

  handleCancelClick() {
    this.modalRef.close();
  }

  handleContinueClick(currentReportStep: ReportStep) {
    const nextStep = currentReportStep + 1;
    if (!(nextStep in ReportStep)) {
      // TO DO: Save user report
      this.modalRef.close();
    }

    this.currentReportStepSubject$.next(nextStep);
  }
}
